/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { Link } from 'react-router-dom';
import PolicyLayout from '../../components/policies/PolicyLayout';

export const RefundPolicyPage = () => (
  <PolicyLayout
    title="Refund Policy"
    effectiveDate="1 February 2026"
    lastUpdated="1 February 2026"
  >
    <p>
      This Refund Policy explains how refund requests are handled on the StudioScheduler platform.
      It applies to all purchases you make through StudioScheduler — including single-class
      bookings, Class Passes, memberships, and Studio point-of-sale (POS) transactions.
    </p>

    <h2>1. StudioScheduler does not issue direct refunds</h2>
    <p>
      <strong>
        StudioScheduler does not offer refunds directly for products or services sold by Studios.
      </strong>{' '}
      When you purchase a class, Class Pass, membership, or any other item through the Platform,
      you enter into a contract directly with the Studio that offers it. Payment is settled to the
      Studio (net of platform and processing fees). Any refund decision is therefore made by the
      Studio, in accordance with their own refund and cancellation policy.
    </p>
    <p>
      Please contact the Studio directly to enquire about their refund policy before or after a
      purchase. Studio contact details, cancellation windows, and refund terms are shown on each
      Studio's profile page and in your booking confirmation email.
    </p>

    <h2>2. How Studio refunds work</h2>
    <p>
      Each Studio sets:
    </p>
    <ul>
      <li>The <strong>cancellation notice period</strong> (typically 12–24 hours before class start);</li>
      <li>Whether cancellations within that window are eligible for a refund, a Class Pass credit, or nothing;</li>
      <li>Class Pass and membership expiry rules;</li>
      <li>Whether "no-show" bookings can be refunded.</li>
    </ul>
    <p>
      When you cancel a booking through the Platform within a Studio's eligible refund window, the
      refund is issued automatically:
    </p>
    <ul>
      <li>
        <strong>Card-paid bookings</strong> are refunded back to the original card via Ryft Payments.
        Bank posting times vary but typically take 5–10 business days.
      </li>
      <li>
        <strong>Class Pass credits</strong> are returned to your Class Pass balance immediately.
      </li>
      <li>
        <strong>Free bookings</strong> require no refund.
      </li>
    </ul>
    <p>
      Where a Studio's policy is that no refund is due (for example, cancellation within the notice
      period, or a no-show), no refund will be issued and you will receive an email confirming the
      cancellation without refund.
    </p>

    <h2>3. Contact the Studio first</h2>
    <p>
      If you believe a refund is due but the Platform has not issued one automatically — for
      example, if the class was cancelled by the Studio, the instructor was different from what was
      advertised, or you were charged in error — <strong>contact the Studio directly</strong> as your
      first step. Studio email addresses and phone numbers are on each Studio's profile page and on
      your receipt.
    </p>
    <p>
      Studios are able to issue full or partial refunds at their discretion from the Platform. They
      will typically respond within 5 business days.
    </p>

    <h2>4. StudioScheduler as an escalation & appeal point</h2>
    <p>
      If you have contacted the Studio and (a) received no response within a reasonable time
      (10 business days), (b) are dissatisfied with the Studio's decision, or (c) suspect fraud or
      a platform error, you may escalate to StudioScheduler.
    </p>
    <p>
      By default, StudioScheduler <strong>mediates</strong> between you and the Studio and will
      encourage a fair resolution. StudioScheduler does not have authority to overturn a Studio's
      refund decision made in good faith and in accordance with their stated policy.
    </p>
    <p>
      However, StudioScheduler <strong>does</strong> have authority to issue or compel a refund at
      its sole discretion in the following limited "platform-error" cases:
    </p>
    <ul>
      <li>
        <strong>Verifiable platform bug</strong> that caused an incorrect charge, duplicate charge,
        or an inability to redeem a valid Class Pass.
      </li>
      <li>
        <strong>Confirmed fraudulent transaction</strong> on your account, following our
        investigation and (where applicable) the payment provider's chargeback process.
      </li>
      <li>
        <strong>Studio unavailable</strong> — the Studio has ceased trading, is unreachable through
        all reasonable channels for more than 30 days, or has otherwise breached its obligations
        under our Studio Terms in a manner that prevents any resolution.
      </li>
      <li>
        <strong>Regulatory or legal obligation</strong> requiring StudioScheduler to issue a refund
        (for example, under Irish or EU consumer protection law).
      </li>
    </ul>
    <p>
      In all other cases the Studio's decision, made in accordance with their published policy, is
      final.
    </p>

    <h2>5. How to raise an appeal</h2>
    <p>Send an appeal to <a href="mailto:appeals@studioscheduler.ie">appeals@studioscheduler.ie</a> with:</p>
    <ul>
      <li>Your registered email address;</li>
      <li>The Studio name and the booking / Class Pass ID (found on your confirmation email);</li>
      <li>A clear description of what happened and the outcome you're seeking;</li>
      <li>Any correspondence with the Studio, and receipts or screenshots if available.</li>
    </ul>
    <p>
      We aim to acknowledge appeals within <strong>2 business days</strong> and reach a decision
      within <strong>14 business days</strong>. Complex cases (for example, those requiring the
      payment provider's investigation) may take longer; we will keep you informed.
    </p>

    <h2>6. Statutory rights</h2>
    <p>
      Nothing in this Refund Policy affects any rights you have under applicable consumer
      protection law, including but not limited to Directive (EU) 2011/83/EU on consumer rights and
      the Irish Consumer Rights Act 2022. Where a statutory right of withdrawal or refund applies,
      it will be honoured in accordance with the law, regardless of Studio policy.
    </p>

    <h2>7. Chargebacks</h2>
    <p>
      If you dispute a charge directly with your card issuer without first exhausting the appeal
      process above, StudioScheduler may temporarily suspend your account pending the outcome of the
      dispute, and will cooperate fully with the payment provider and your bank. Successful
      chargebacks may result in the loss of any classes booked or Class Passes purchased with the
      disputed amount.
    </p>

    <h2>8. Changes to this Refund Policy</h2>
    <p>
      We may update this Refund Policy from time to time. The "Last updated" date at the top of the
      page shows when the most recent change took effect. Continued use of the Platform after that
      date constitutes acceptance of the updated policy.
    </p>

    <h2>9. Related policies</h2>
    <p>
      See our <Link to="/terms">Terms &amp; Conditions</Link> and{' '}
      <Link to="/privacy">Privacy Policy</Link> for the wider terms that govern your use of the
      Platform.
    </p>
  </PolicyLayout>
);

export default RefundPolicyPage;
